import { api } from "./api";
import { getAll as get } from "./pagination";
export interface Department {
  id: number;
  name: string;
  code: string;
  description?: string;
  department_type:
    | "academic"
    | "administrative"
    | "finance"
    | "student_services"
    | "operations"
    | "other";
  head?: number | null;
  head_name?: string;
  is_active: boolean;
  head_changes?: DepartmentHeadChange[];
  /** Whether a delete would be accepted -- the server applies the same rule
   * its destroy() does, so the UI never offers a button that will be refused. */
  can_delete?: boolean;
  /** What is still using it, e.g. {staff: 3, positions: 1}. Empty when free. */
  in_use_by?: Record<string, number>;
}
export interface DepartmentHeadChange {
  id: number;
  previous_head?: number | null;
  previous_head_name?: string;
  new_head?: number | null;
  new_head_name?: string;
  changed_by: number;
  changed_by_name: string;
  reason: string;
  created_at: string;
}
export interface Position {
  id: number;
  title: string;
  description?: string | null;
  qualifications?: string | null;
  department: number | null;
  department_name: string | null;
  level: string;
  experience_required: number;
  salary_range_min?: string | null;
  salary_range_max?: string | null;
  is_active: boolean;
  is_vacant: boolean;
  can_delete?: boolean;
  in_use_by?: Record<string, number>;
}
/** One named allowance -- "Housing", 200000 -- granted by an approved pay change. */
export interface AllowanceLine {
  name: string;
  amount: string;
}
export interface StaffProfile {
  id: number;
  user: number;
  user_name: string;
  user_username: string;
  user_type: string;
  employee_id: string;
  position: number;
  position_title: string;
  department: number | null;
  department_name: string | null;
  hire_date: string;
  employment_status: string;
  work_location?: string;
  salary?: string | null;
  /** Total of the named lines below; part of gross for tax. Derived, never typed. */
  allowances?: string | null;
  allowance_lines?: AllowanceLine[];
  currency: string;
  /** Payroll identifiers and bank details - see StaffProfile on the backend. */
  tin?: string;
  nssf_number?: string;
  /** Decides which PAYE bands apply. */
  tax_residency?: "resident" | "non_resident";
  bank_name?: string;
  bank_branch?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  payment_frequency: "monthly" | "weekly" | "daily";
  reporting_manager_name?: string;
  /** Department(s) this person heads (Department.head), distinct from the department they belong to. */
  headed_departments?: Array<{ id: number; name: string }>;
  employment_changes?: Array<{
    id: number;
    previous_status: string;
    new_status: string;
    effective_date: string;
    reason: string;
    changed_by_name: string;
  }>;
}
export interface SalaryChangeRequest {
  id: number;
  staff: number;
  staff_name: string;
  previous_salary: string;
  proposed_salary: string;
  previous_allowances: AllowanceLine[];
  proposed_allowances: AllowanceLine[];
  currency: string;
  effective_date: string;
  reason: string;
  workflow_status: string;
  initiated_by: number;
  initiated_by_name: string;
  approved_by_name?: string;
  review_comments?: string;
  rejection_reason?: string;
  return_reason?: string;
  created_at: string;
}
export interface LeaveType {
  id: number;
  name: string;
  code: string;
  description?: string | null;
  max_days_per_year: number;
  is_paid: boolean;
  requires_approval: boolean;
  is_active: boolean;
  can_delete?: boolean;
  in_use_by?: Record<string, number>;
}
export interface LeaveRequest {
  id: number;
  staff: number;
  staff_name: string;
  leave_type: number;
  leave_type_name: string;
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: string;
  approved_by_name?: string;
  approved_date?: string | null;
  approval_notes?: string;
  created_at?: string;
}
export interface Attendance {
  id: number;
  staff: number;
  staff_name: string;
  date: string;
  check_in_time?: string;
  check_out_time?: string;
  status: string;
  hours_worked: string;
  notes?: string;
}
export interface Training {
  id: number;
  name: string;
  training_type: string;
  start_date: string;
  end_date: string;
  duration_hours: number;
  venue?: string;
  trainer?: string;
  organizer_name: string;
  is_mandatory: boolean;
  is_active: boolean;
  description?: string;
  objectives?: string;
}
export interface TrainingAttendance {
  id: number;
  training: number;
  training_name: string;
  staff: number;
  staff_name: string;
  attended: boolean;
  score?: string | null;
  passed: boolean;
  completion_date?: string | null;
  certificate_number?: string | null;
  feedback?: string | null;
}
export interface EligibleUser {
  id: number;
  full_name: string;
  user_type: string;
  email: string;
}
export interface StaffStats {
  total_staff: number;
  total_departments: number;
  staff_on_leave: number;
  active_staff: number;
  pending_leave: number;
}
export const staffService = {
  getDepartments: () => get<Department>("/staff/departments/"),
  /** Refused with 409 when the department still has staff, positions or
   * leadership history; the server's message says which. */
  deleteDepartment: (id: number) => api.delete(`/staff/departments/${id}/`),
  setDepartmentActive: async (id: number, is_active: boolean) =>
    (await api.patch<Department>(`/staff/departments/${id}/`, { is_active })).data,
  createDepartment: async (d: Partial<Department>) =>
    (await api.post("/staff/departments/", d)).data,
  /** The head is not editable here; it changes through assignDepartmentHead, which records why. */
  updateDepartment: async ({ id, ...d }: Partial<Department> & { id: number }) =>
    (await api.patch<Department>(`/staff/departments/${id}/`, d)).data,
  assignDepartmentHead: async ({
    id,
    head,
    reason,
  }: {
    id: number;
    head: number | null;
    reason: string;
  }) =>
    (await api.post(`/staff/departments/${id}/assign-head/`, { head, reason }))
      .data,
  getPositions: () => get<Position>("/staff/positions/"),
  /** Refused with 409 when anyone has ever held the position. */
  deletePosition: (id: number) => api.delete(`/staff/positions/${id}/`),
  setPositionActive: async (id: number, is_active: boolean) =>
    (await api.patch<Position>(`/staff/positions/${id}/`, { is_active })).data,
  createPosition: async (d: Partial<Position>) =>
    (await api.post("/staff/positions/", d)).data,
  updatePosition: async ({ id, ...d }: Partial<Position> & { id: number }) =>
    (await api.patch<Position>(`/staff/positions/${id}/`, d)).data,
  getProfiles: () => get<StaffProfile>("/staff/profiles/"),
  createProfile: async (d: Partial<StaffProfile>) =>
    (await api.post("/staff/profiles/", d)).data,
  updateProfile: async (id: number, d: Partial<StaffProfile>) =>
    (await api.patch(`/staff/profiles/${id}/`, d)).data,
  offboard: async ({
    id,
    ...data
  }: {
    id: number;
    employment_status: string;
    effective_date: string;
    reason: string;
  }) => (await api.post(`/staff/profiles/${id}/offboard/`, data)).data,
  getSalaryChangeRequests: () =>
    get<SalaryChangeRequest>("/staff/salary-change-requests/"),
  createSalaryChangeRequest: async (data: {
    staff: number;
    proposed_salary: string;
    proposed_allowances: AllowanceLine[];
    effective_date: string;
    reason: string;
  }) => (await api.post("/staff/salary-change-requests/", data)).data,
  approveSalaryChange: async (id: number) =>
    (await api.post(`/staff/salary-change-requests/${id}/approve/`, {})).data,
  rejectSalaryChange: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/staff/salary-change-requests/${id}/reject/`, { reason })).data,
  returnSalaryChange: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/staff/salary-change-requests/${id}/return/`, { reason })).data,
  reviseSalaryChange: async ({
    id,
    ...data
  }: {
    id: number;
    proposed_salary: string;
    proposed_allowances: AllowanceLine[];
    effective_date: string;
    reason: string;
  }) => (await api.post(`/staff/salary-change-requests/${id}/revise/`, data)).data,
  getLeaveTypes: () => get<LeaveType>("/staff/leave-types/"),
  /** Refused with 409 when leave has ever been taken under the policy --
   * deleting it would cascade and take that leave history with it. */
  deleteLeaveType: (id: number) => api.delete(`/staff/leave-types/${id}/`),
  setLeaveTypeActive: async (id: number, is_active: boolean) =>
    (await api.patch<LeaveType>(`/staff/leave-types/${id}/`, { is_active })).data,
  createLeaveType: async (d: Partial<LeaveType>) =>
    (await api.post("/staff/leave-types/", d)).data,
  updateLeaveType: async ({ id, ...d }: Partial<LeaveType> & { id: number }) =>
    (await api.patch<LeaveType>(`/staff/leave-types/${id}/`, d)).data,
  getLeave: () => get<LeaveRequest>("/staff/leave-requests/"),
  requestLeave: async (d: Partial<LeaveRequest>) =>
    (await api.post("/staff/leave-requests/", d)).data,
  approveLeave: async ({ id, notes }: { id: number; notes?: string }) =>
    (await api.post(`/staff/leave-requests/${id}/approve/`, { notes: notes || undefined })).data,
  rejectLeave: async ({ id, notes }: { id: number; notes?: string }) =>
    (await api.post(`/staff/leave-requests/${id}/reject/`, { notes: notes || undefined })).data,
  cancelLeave: async (id: number) =>
    (await api.post(`/staff/leave-requests/${id}/cancel/`, {})).data,
  getAttendance: () => get<Attendance>("/staff/attendance/"),
  createAttendance: async (d: Partial<Attendance>) =>
    (await api.post("/staff/attendance/", d)).data,
  getTraining: () => get<Training>("/staff/training/"),
  createTraining: async (d: Partial<Training>) =>
    (await api.post("/staff/training/", d)).data,
  getTrainingAttendance: () =>
    get<TrainingAttendance>("/staff/training-attendance/"),
  createTrainingAttendance: async (d: Partial<TrainingAttendance>) =>
    (await api.post("/staff/training-attendance/", d)).data,
  getEligibleUsers: async () =>
    (await api.get("/staff/eligible-users/")).data as EligibleUser[],
  getStats: async () =>
    (await api.get("/staff/statistics/")).data as StaffStats,
};
